import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { useAuth } from '@/contexts/AuthContext';
import { MSAService } from '@/types';
import { useNotification } from '@/hooks/common';
import { NOTIFICATION } from '@/constants';
import { useCustomerUserServices, useCustomerUserSearch, useCustomerUserActions } from '@/hooks/customerUser';
import { SubscribedServicesTab } from '@/components/services/management/user/tabs/SubscribedServicesTab';
import { ServiceSearchTab } from '@/components/services/management/user/tabs/ServiceSearchTab';
import { MyServicesTab } from '@/components/services/registration/MyServicesTab';
import { ServiceDetailDialog } from '@/components/common/shared/ServiceDetailDialog';
import ConfirmDialog from '@/components/users/management/common/dialogs/ConfirmDialog';

interface CustomerUserDashboardProps {
  initialTab?: 'services' | 'search' | 'mine' | 'users' | 'logs';
}

const CustomerUserDashboard: React.FC<CustomerUserDashboardProps> = ({ initialTab }) => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as 'services' | 'search' | 'mine' | 'users' | 'logs' | null;
  
  // URL에서 직접 읽어서 항상 최신 값 사용 (상태 동기화 문제 방지)
  const activeTab: 'services' | 'search' | 'mine' | 'users' | 'logs' = tabFromUrl || initialTab || 'services';
  
  const { error, success, setError, setSuccess } = useNotification({ duration: NOTIFICATION.DEFAULT_DURATION });
  const isAdmin = user?.role === 'admin';

  // Hooks를 사용하여 상태와 로직 관리
  const {
    subscribedServices,
    servicesLoading,
    healthStatuses,
    subscribedServiceIds,
    setSubscribedServiceIds,
    setHealthStatuses,
    fetchSubscribedServices,
  } = useCustomerUserServices();

  const {
    searchResults,
    searchLoading,
    handleSearch,
  } = useCustomerUserSearch(setSubscribedServiceIds, setHealthStatuses, setError);

  const {
    handleSubscribe,
    handleUnsubscribe,
    handleServiceConnect,
  } = useCustomerUserActions({
    customerId: user?.customer?.id,
    isAdmin,
    setError,
    setSuccess,
    fetchUsers: () => {},
    fetchSubscribedServices,
    handleSearch,
    setSubscribedServiceIds,
    activeTab,
    searchResults,
  });

  const [selectedService, setSelectedService] = useState<MSAService | null>(null);
  const [isServiceDetailOpen, setIsServiceDetailOpen] = useState(false);
  const [unsubscribeConfirm, setUnsubscribeConfirm] = useState<{ open: boolean; serviceSlug: string | null }>({ open: false, serviceSlug: null });

  // URL 파라미터 초기화 (한 번만)
  const hasInitializedTab = useRef(false);
  useEffect(() => {
    if (!hasInitializedTab.current && !tabFromUrl) {
      hasInitializedTab.current = true;
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [tabFromUrl, activeTab, setSearchParams]);

  const handleTabChange = (tab: string) => {
    const validTab = tab as 'services' | 'search' | 'users' | 'logs';
    // 즉시 URL 업데이트 (상태는 URL에서 자동으로 읽어옴)
    setSearchParams({ tab: validTab }, { replace: true });
  };

  // 서비스 클릭 핸들러 (모달 열기)
  const handleServiceClick = (service: MSAService, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setSelectedService(service);
    setIsServiceDetailOpen(true);
  };

  // 탭별 데이터 로드
  useEffect(() => {
    if (activeTab === 'services') {
      fetchSubscribedServices();
    } else if (activeTab === 'search') {
      // 검색 탭이 활성화되면 자동으로 공개 서비스 로드
      // handleSearch는 useCallback으로 메모이제이션되어 있지만,
      // 의존성 배열에서 제거하여 activeTab 변경 시에만 실행되도록 함
      handleSearch('', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // activeTab만 의존성으로 사용하여 탭 변경 시에만 실행

  // 탭 목록 구성
  // 서비스 이용 화면에서는 기업관리자/일반사용자 모두 \"구독중 서비스\" + \"전체 서비스 검색\"만 사용
  const tabList = [
    { value: 'services', label: '구독중 서비스' },
    { value: 'search', label: '전체 서비스 검색' },
    // 내 서비스를 직접 올린다 (#136). 신청은 등록대기로 남고 운영자가 승인한다
    { value: 'mine', label: '내 서비스' },
  ];

  return (
    <div className="space-y-6">
      {error && (
        <AlertMessage 
          type="error" 
          message={error} 
          onClose={() => setError('')} 
        />
      )}
      {success && (
        <AlertMessage 
          type="success" 
          message={success} 
          onClose={() => setSuccess('')} 
        />
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-3">
          {tabList.map(tab => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="services">
          <SubscribedServicesTab
            subscribedServices={subscribedServices}
            servicesLoading={servicesLoading}
            healthStatuses={healthStatuses}
            companyName={user?.customer?.customer_name}
            onRefresh={fetchSubscribedServices}
            onServiceClick={handleServiceClick}
            onConnect={handleServiceConnect}
          />
        </TabsContent>

        <TabsContent value="mine">
          <MyServicesTab />
        </TabsContent>

        <TabsContent value="search">
          <ServiceSearchTab
            searchResults={searchResults}
            searchLoading={searchLoading}
            subscribedServiceIds={subscribedServiceIds}
            healthStatuses={healthStatuses}
            isAdmin={isAdmin}
            onSearch={handleSearch}
            onServiceClick={handleServiceClick}
            onSubscribe={handleSubscribe}
            onUnsubscribe={handleUnsubscribe}
          />
        </TabsContent>
        
        <ServiceDetailDialog
          service={selectedService}
          isOpen={isServiceDetailOpen}
          onClose={() => {
            setIsServiceDetailOpen(false);
            setSelectedService(null);
          }}
          healthStatuses={healthStatuses}
          isSubscribed={selectedService ? subscribedServiceIds.has(selectedService.id) : false}
          onSubscribe={handleSubscribe}
          onUnsubscribe={(serviceSlug) => setUnsubscribeConfirm({ open: true, serviceSlug })}
          onConnect={handleServiceConnect}
        />

        {/* 사용자 관리 / 활동 로그 탭은 서비스 이용 화면에서 제거 (설정 메뉴에서 제공) */}
      </Tabs>

      <ConfirmDialog
        open={unsubscribeConfirm.open}
        title="서비스 구독 해제"
        description="정말로 이 서비스 구독을 해제하시겠습니까?"
        confirmText="구독 해제"
        cancelText="취소"
        variant="destructive"
        onConfirm={async () => {
          if (!unsubscribeConfirm.serviceSlug) return;
          try {
            await handleUnsubscribe(unsubscribeConfirm.serviceSlug);
            setUnsubscribeConfirm({ open: false, serviceSlug: null });
          } catch (error) {
            // Error is handled by hook
            setUnsubscribeConfirm({ open: false, serviceSlug: null });
          }
        }}
        onCancel={() => setUnsubscribeConfirm({ open: false, serviceSlug: null })}
      />

      {/* 일반 사용자 대시보드에서는 사용자 관리(비활성화/삭제)를 제공하지 않으므로
          관련 ConfirmDialog를 제거합니다. */}
    </div>
  );
};

export default CustomerUserDashboard;
